/**
 * Usage Chart Page
 * Shows monthly parking hours usage chart
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Alert,
  Dropdown,
} from 'react-bootstrap';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
  ReferenceLine,
} from 'recharts';
import {
  getMonthlyParkingStats,
  getAvailableMonths,
} from '../services/api';
import {
  getCurrentMonth,
  formatMonthLabel,
  getParkingHoursColor,
} from '../utils/helpers';
import LoadingSpinner from '../components/LoadingSpinner';
import EmptyState from '../components/EmptyState';

const UsageChart = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  const [monthlyStats, setMonthlyStats] = useState([]);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [statsLoading, setStatsLoading] = useState(false);

  const fetchMonthlyStats = useCallback(async (month) => {
    try {
      setStatsLoading(true);
      const monthsData = await getAvailableMonths();
      setAvailableMonths(monthsData);

      // If no months available, stop here
      if (monthsData.length === 0) {
        setMonthlyStats([]);
        setStatsLoading(false);
        return;
      }

      // Use the first available month if the requested month isn't in the list
      const actualMonth = monthsData.includes(month) ? month : monthsData[0];
      if (actualMonth !== month) {
        setSelectedMonth(actualMonth);
      }

      const statsData = await getMonthlyParkingStats(actualMonth);

      // Transform data for chart
      const chartData = statsData.map(item => ({
        licensePlate: item.license_plate,
        hours: parseFloat(item.total_hours) || 0,
        registrations: parseInt(item.registration_count) || 0,
        remaining: Math.max(0, 250 - (parseFloat(item.total_hours) || 0)),
      }));

      setMonthlyStats(chartData);
    } catch (err) {
      console.error('Error fetching monthly stats:', err);
      setError(err.message);
    } finally {
      setStatsLoading(false);
      setLoading(false);
    }
  }, []);

  // Fetch monthly stats when month changes
  useEffect(() => {
    fetchMonthlyStats(selectedMonth);
  }, [selectedMonth, fetchMonthlyStats]);

  // Custom label component for remaining hours (right side of bar)
  const CustomLabel = (props) => {
    const { x, y, width, height, value, remaining } = props;

    // Position text to the right of the bar
    const labelX = x + width + 10;
    const centerY = y + height / 2;

    return (
      <text
        x={labelX}
        y={centerY}
        fill="#666"
        textAnchor="start"
        dominantBaseline="middle"
        fontSize="12"
      >
        Remaining {remaining}h
      </text>
    );
  };

  if (loading) {
    return <LoadingSpinner message="Loading usage chart..." />;
  }

  return (
    <Container>
      <Row className="mb-4">
        <Col>
          <h2>Monthly Parking Hours Usage</h2>
          <p className="text-muted">
            View monthly parking hours usage for all license plates.
          </p>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          Error: {error}
        </Alert>
      )}

      {/* Monthly Parking Hours Chart */}
      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center">
                <span>Monthly Parking Hours Usage</span>
                <div className="d-flex align-items-center gap-2">
                  {availableMonths.length > 0 ? (
                    <>
                      <i className="bi bi-calendar3 text-muted"></i>
                      <small className="text-muted me-1">Month:</small>
                      <Dropdown>
                        <Dropdown.Toggle variant="outline-secondary" size="sm" id="month-dropdown">
                          {formatMonthLabel(selectedMonth)}
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          {availableMonths.map((month) => (
                            <Dropdown.Item
                              key={month}
                              active={month === selectedMonth}
                              onClick={() => setSelectedMonth(month)}
                            >
                              {formatMonthLabel(month)}
                            </Dropdown.Item>
                          ))}
                        </Dropdown.Menu>
                      </Dropdown>
                    </>
                  ) : (
                    <small className="text-muted">No data available</small>
                  )}
                </div>
              </div>
            </Card.Header>
            <Card.Body>
              <div className="mb-3 text-muted small">
                <i className="bi bi-info-circle me-1"></i>
                Monthly quota for each license plate: 250 hours. Chart displays total usage across all members with the same plate.
              </div>

              {statsLoading ? (
                <LoadingSpinner message="Loading chart..." />
              ) : availableMonths.length === 0 ? (
                <EmptyState icon="bar-chart" message="No parking data available yet" />
              ) : monthlyStats.length === 0 ? (
                <EmptyState icon="bar-chart" message="No parking data for this month" />
              ) : (
                <ResponsiveContainer width="100%" height={Math.max(400, monthlyStats.length * 60)}>
                  <BarChart
                    data={monthlyStats}
                    layout="vertical"
                    margin={{
                      top: 35,
                      right: window.innerWidth < 768 ? 70 : 120,
                      left: 5,
                      bottom: 25
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis
                      type="number"
                      domain={[0, 300]}
                      orientation="top"
                      tick={{ fontSize: 12 }}
                      label={{
                        value: 'Hours',
                        position: 'insideTopRight',
                        offset: -5,
                        style: { fontSize: 11, fill: '#666', fontStyle: 'italic' }
                      }}
                    />
                    <YAxis
                      type="category"
                      dataKey="licensePlate"
                      width={window.innerWidth < 768 ? 70 : 90}
                      tick={{ fontSize: 11 }}
                    />
                    <ReferenceLine
                      x={250}
                      stroke="#6c757d"
                      strokeDasharray="5 5"
                      strokeWidth={2}
                      label={{
                        value: '250h Quota',
                        position: 'insideBottom',
                        fill: '#6c757d',
                        fontSize: 12,
                        fontWeight: 'bold',
                        offset: 10
                      }}
                    />
                    <Tooltip
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          return (
                            <div style={{
                              backgroundColor: '#fff',
                              padding: '10px',
                              border: '1px solid #ccc',
                              borderRadius: '4px',
                            }}>
                              <p style={{ margin: 0, fontWeight: 'bold' }}>{data.licensePlate}</p>
                              <p style={{ margin: '5px 0 0 0', color: '#666' }}>
                                Used: {data.hours}h / 250h
                              </p>
                              <p style={{ margin: '5px 0 0 0', color: '#666' }}>
                                Remaining: {data.remaining}h
                              </p>
                              <p style={{ margin: '5px 0 0 0', color: '#999', fontSize: '0.85em' }}>
                                {data.registrations} registration{data.registrations !== 1 ? 's' : ''}
                              </p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Bar
                      dataKey="hours"
                      barSize={40}
                      radius={[0, 8, 8, 0]}
                    >
                      {monthlyStats.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={getParkingHoursColor(entry.hours)} />
                      ))}
                      <LabelList
                        dataKey="hours"
                        content={(props) => (
                          <CustomLabel
                            {...props}
                            remaining={monthlyStats[props.index]?.remaining || 0}
                          />
                        )}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}

              {/* Legend */}
              <div className="d-flex justify-content-center gap-4 mt-3">
                <div className="d-flex align-items-center gap-2">
                  <div style={{
                    width: '16px',
                    height: '16px',
                    backgroundColor: '#28a745',
                    borderRadius: '3px'
                  }}></div>
                  <span style={{ fontSize: '11px', color: '#6c757d' }}>&lt; 200h</span>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <div style={{
                    width: '16px',
                    height: '16px',
                    backgroundColor: '#ffc107',
                    borderRadius: '3px'
                  }}></div>
                  <span style={{ fontSize: '11px', color: '#6c757d' }}>200-240h</span>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <div style={{
                    width: '16px',
                    height: '16px',
                    backgroundColor: '#dc3545',
                    borderRadius: '3px'
                  }}></div>
                  <span style={{ fontSize: '11px', color: '#6c757d' }}>&gt; 240h</span>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default UsageChart;
